import React, { useRef } from 'react';
import { InputRef, Space } from 'antd';
import { Form, Input } from 'antd';
import { FormInstance } from 'antd/lib/form/Form';

interface Props {
  form: FormInstance;
}
const DateOfBirth: React.FC<Props> = ({ form }) => {
  const monthRef = useRef<InputRef>(null);
  const dayRef = useRef<InputRef>(null);
  const yearRef = useRef<InputRef>(null);

  const inputStyles = {
    margin: '0 8px',
    width: '100px',
    marginBottom: 0,
  };

  const requiredRule = {
    required: true,
    message: 'This field is required.',
  };

  const handleDOBChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { maxLength, value, name } = e.target;
    if (value !== '0' && !Number(value)) {
      // if user has deleted the field values, or a bad value is entered, it
      // resets the field.
      form.setFieldValue(name, '');
      if (name === 'year' && dayRef.current) {
        // focuses on the previous element after deleting
        dayRef.current.focus();
      }
      if (name === 'day' && monthRef.current) {
        monthRef.current.focus();
      }
      return;
    }
    if (value.length >= maxLength) {
      // focuses on the next element after reaching the max length
      if (name === 'month' && dayRef.current) {
        dayRef.current.focus();
      }
      else if (name === 'day' && yearRef.current) {
        yearRef.current.focus();
      }
    }
  };

  return (
    <Form.Item
      label="Date of Birth"
      name="dateOfBirth"
      rules={[
        {
          required: true,
          message: 'Please input your date of birth.',
        },
      ]}
    >
      <Space.Compact style={{ display: 'flex' }}>
        <Form.Item
          name="month"
          data-cy="date_of_birth"
          rules={[
            requiredRule,
            {
              min: 2,
              max: 2,
              message: 'Please enter 2 digits.',
            },
            () => ({
              transform: value => Number(value),
              validator(_, value) {
                if (value <= 12) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('Please enter a valid month.'));
              },
            }),
          ]}
          style={{ ...inputStyles, marginLeft: 0 }}
          validateTrigger="onBlur"
        >
          <Input
            name="month"
            placeholder="MM"
            ref={monthRef}
            minLength={2}
            maxLength={2}
            onChange={handleDOBChange}
            inputMode="numeric"
            data-cy="month"
            style={{ borderRadius: '6px' }}
          />
        </Form.Item>
        <Form.Item
          name="day"
          rules={[
            requiredRule,
            {
              min: 2,
              max: 2,
              message: 'Please enter 2 digits.',
            },
            {
              transform: value => Number(value),
              validator: (_, value) => {
                if (value <= 31) {
                  return Promise.resolve();
                }
                return Promise.reject(new Error('Please enter a valid day.'));
              },
            },
          ]}
          style={inputStyles}
          validateTrigger="onBlur"
        >
          <Input
            name="day"
            ref={dayRef}
            placeholder="DD"
            minLength={2}
            maxLength={2}
            onChange={handleDOBChange}
            inputMode="numeric"
            data-cy="day"
            style={{ borderRadius: '6px' }}
          />
        </Form.Item>
        <Form.Item
          name="year"
          rules={[
            requiredRule,
            {
              min: 4,
              max: 4,
              message: 'Please enter 4 digits.',
            },
            () => ({
              message: 'Please enter a valid year.',
              transform: value => Number(value),
              validator(_, value) {
                if (value <= new Date().getFullYear() && value > 1900) {
                  return Promise.resolve();
                }
                return Promise.reject();
              },
            }),
          ]}
          style={inputStyles}
          validateTrigger="onBlur"
        >
          <Input
            name="year"
            ref={yearRef}
            placeholder="YYYY"
            minLength={4}
            maxLength={4}
            onChange={handleDOBChange}
            inputMode="numeric"
            data-cy="year"
            style={{ borderRadius: '6px' }}
          />
        </Form.Item>
      </Space.Compact>
    </Form.Item>
  );
};

export default DateOfBirth;
